import React, { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Plus, Users, ArrowLeft, Merge, Bell } from 'lucide-react';

import CustomerDetailModal, {
  Customer,
} from '../components/modals/CustomerDetailModal';
import NewCustomerModal from '../components/modals/NewCustomerModal';
import SendNotificationModal from '../components/modals/SendNotificationModal';
import CustomerCard from '../components/customers/CustomerCard';
import DuplicateCustomersView from '../components/customers/DuplicateCustomersView';
import PrimaryButton from '../components/ui/PrimaryButton';

import { useAuth } from '../contexts/AuthContext';
import { useClients } from '../contexts/ClientsContext';
import { useEvents } from '../contexts/EventsContext';
import { mapRawClientToCustomer } from '../utils/customerMapper';
import { useTechnicalNotes } from '../contexts/TechnicalNotesContext';

const Customers: React.FC = () => {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const { clients: rawClients } = useClients();
  const { bookings } = useEvents();
  const { notes: technicalNotes } = useTechnicalNotes();

  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(12);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [isNewCustomerModalOpen, setIsNewCustomerModalOpen] = useState(false);
  const [isSendNotificationOpen, setIsSendNotificationOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'duplicates'>('list');

  // Gestione merge da header
  const [canMerge, setCanMerge] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const triggerMergeRef = useRef<(() => void) | null>(null);

  // Mappiamo i clients del context -> tipo Customer usato da card/modal
  const customers: Customer[] = useMemo(() => {
    if (!rawClients) return [];

    return rawClients
      .filter((c: any) => c.is_active !== false || c.merged_into_id != null)
      .map((c: any): Customer =>
        mapRawClientToCustomer(c, bookings, t('customers.never_visited'))
      );
  }, [rawClients, bookings, t]);

  // Clienti le cui note tecniche contengono il termine cercato: così "6.0"
  // risponde alla domanda "quali clienti usano questa formula?".
  const clientIdsMatchingNotes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const ids = new Set<string>();
    if (!term) return ids;
    technicalNotes.forEach((n) => {
      if ((n.content || '').toLowerCase().includes(term)) ids.add(String(n.client_id));
    });
    return ids;
  }, [technicalNotes, searchTerm]);

  // Filtro ricerca: anagrafica + note tecniche
  const filteredCustomers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return customers;

    return customers.filter((c) => {
      return (
        c.firstName.toLowerCase().includes(term) ||
        c.lastName.toLowerCase().includes(term) ||
        (c.email && c.email.toLowerCase().includes(term)) ||
        (c.phone && c.phone.toLowerCase().includes(term)) ||
        clientIdsMatchingNotes.has(String(c.id))
      );
    });
  }, [customers, searchTerm, clientIdsMatchingNotes]);

  return (
    <div className="max-w-7xl mx-auto space-y-8 relative">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative">
        <div className={`flex items-center gap-3 ${viewMode === 'duplicates' ? 'pr-16 md:pr-0' : ''}`}>
          {viewMode === 'duplicates' && (
            <button
              onClick={() => setViewMode('list')}
              className="p-2 text-gray-500 hover:text-clip-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
          )}
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {t('customers.title')}
            </h1>
            <p className="text-gray-500 mt-1">{t('customers.subtitle')}</p>
          </div>
        </div>

        <div className={`flex items-center gap-3 ${
          viewMode === 'duplicates' 
            ? 'absolute right-0 top-0 md:static w-auto' 
            : 'w-full md:w-auto'
        }`}>
          {viewMode === 'list' ? (
            <>
              {/* Search Bar */}
              <div className="relative flex-1 md:w-80 group">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-clip-500 transition-colors"
                  size={20}
                />
                <input
                  type="text"
                  placeholder={t('common.search') as string}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setVisibleCount(12);
                  }}
                  className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-clip-200 focus:border-clip-400 transition-all shadow-sm placeholder:text-gray-400"
                />
              </div>

              <PrimaryButton
                label={t('customers.discover_duplicates')}
                icon={Users}
                onClick={() => setViewMode('duplicates')}
                variant="secondary"
              />

              {/* Invio notifiche: riservato al titolare */}
              {isAdmin && (
                <PrimaryButton
                  label={t('broadcast.button', 'Invia notifica')}
                  icon={Bell}
                  onClick={() => setIsSendNotificationOpen(true)}
                  variant="secondary"
                />
              )}

              <PrimaryButton
                label={t('customers.new_customer')}
                icon={Plus}
                onClick={() => setIsNewCustomerModalOpen(true)}
              />
            </>
          ) : (
            <button
                onClick={() => triggerMergeRef.current?.()}
                disabled={!canMerge || isMerging}
                className={`flex items-center gap-2 p-3 md:px-6 md:py-2.5 rounded-xl font-bold text-white shadow-sm transition-all ${canMerge && !isMerging
                    ? 'bg-clip-500 hover:bg-clip-600 hover:-translate-y-0.5'
                    : 'bg-gray-300 cursor-not-allowed opacity-50'
                    }`}
            >
                <Merge size={18} />
                <span className="hidden md:inline">
                    {isMerging ? t('customers.duplicates.merging') : t('customers.duplicates.merge_selected')}
                </span>
            </button>
          )}
        </div>
      </div>

      {viewMode === 'duplicates' ? (
        <DuplicateCustomersView
          customers={customers}
          onMergeSuccess={() => setViewMode('list')}
          onSelectionChange={setCanMerge}
          onIsMergingChange={setIsMerging}
          bindTriggerMerge={(trigger) => triggerMergeRef.current = trigger}
        />
      ) : (
        <>
          {filteredCustomers.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-2xl">
              <Users size={24} className="mx-auto text-gray-300 mb-2" />
              <p className="text-gray-400 text-sm">Non ci sono ancora clienti.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredCustomers.slice(0, visibleCount).map((client) => (
                  <CustomerCard
                    key={client.id}
                    customer={client}
                    onClick={() => setSelectedCustomer(client)}
                  />
                ))}
              </div>

              {filteredCustomers.length > visibleCount && (
                <div className="flex justify-center pt-8 pb-12">
                  <PrimaryButton
                    variant="secondary"
                    label={t('common.show_more')}
                    onClick={() => setVisibleCount((prev) => prev + 12)}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Customer Detail Modal */}
      <CustomerDetailModal
        customer={selectedCustomer}
        onClose={() => setSelectedCustomer(null)}
      />

      {/* New Customer Modal */}
      <NewCustomerModal
        isOpen={isNewCustomerModalOpen}
        onClose={() => setIsNewCustomerModalOpen(false)}
      />

      {/* Invia notifica ai clienti */}
      <SendNotificationModal
        isOpen={isSendNotificationOpen}
        onClose={() => setIsSendNotificationOpen(false)}
      />
    </div>
  );
};

export default Customers;
